import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Clock, CheckCircle, Search, SlidersHorizontal,
  MoreVertical, Archive, Trash2, ArchiveRestore, X, ChevronDown,
  Plus, RefreshCw, Globe, AlertCircle, PenLine,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSpace } from '../context/SpaceContext';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  generating: { label: 'En génération', color: 'text-[#ffd93d]', bg: 'bg-[#ffd93d]/10', border: 'border-[#ffd93d]/30' },
  ready:      { label: 'Prêt',          color: 'text-[#20c997]', bg: 'bg-[#20c997]/10', border: 'border-[#20c997]/30' },
  error:      { label: 'Erreur',        color: 'text-[#ff6b6b]', bg: 'bg-[#ff6b6b]/10', border: 'border-[#ff6b6b]/30' },
};

// ── Course context menu ────────────────────────────────────────────────────────
function CourseMenu({ course, onArchive, onUnarchive, onDelete }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isArchived = !!course.archived_at;

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (!e.target.closest(`[data-menuid="lib-${course.id}"]`)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, course.id]);

  return (
    <div data-menuid={`lib-${course.id}`} className="relative" onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(v => !v)}
        data-testid={`lib-menu-${course.id}`}
        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 text-[#64748b] hover:text-white transition-colors"
      >
        <MoreVertical size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-[#1a1927] border border-white/10 rounded-xl shadow-xl z-50 min-w-[155px] overflow-hidden">
          <button onClick={() => { setOpen(false); navigate(`/course/${course.id}/edit`); }}
            data-testid={`lib-edit-${course.id}`}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-[#e8e8e8] hover:bg-white/5 transition-colors">
            <PenLine size={13} className="text-[#20c997]" /> Éditer
          </button>
          <div className="border-t border-white/5" />
          {!isArchived ? (
            <button onClick={() => { setOpen(false); onArchive(course.id); }}
              data-testid={`lib-archive-${course.id}`}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-[#e8e8e8] hover:bg-white/5 transition-colors">
              <Archive size={13} className="text-[#ffd93d]" /> Archiver
            </button>
          ) : (
            <button onClick={() => { setOpen(false); onUnarchive(course.id); }}
              data-testid={`lib-unarchive-${course.id}`}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-[#e8e8e8] hover:bg-white/5 transition-colors">
              <ArchiveRestore size={13} className="text-[#20c997]" /> Restaurer
            </button>
          )}
          <div className="border-t border-white/5" />
          <button onClick={() => { setOpen(false); onDelete(course.id, course.title); }}
            data-testid={`lib-delete-${course.id}`}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-[#ff6b6b] hover:bg-[#ff6b6b]/5 transition-colors">
            <Trash2 size={13} /> Supprimer
          </button>
        </div>
      )}
    </div>
  );
}

// ── Course card ───────────────────────────────────────────────────────────────
function CourseCard({ course, isCompleted, onNavigate, onArchive, onUnarchive, onDelete }) {
  const st = STATUS_CONFIG[course.status] || STATUS_CONFIG.ready;
  const modules = course.modules || [];

  return (
    <div
      className={`group bg-[#16161e] border rounded-2xl p-4 flex flex-col gap-3 transition-all hover:border-[#3a3945] ${course.status === 'error' ? 'opacity-60' : 'border-[#2a2935]'}`}
      data-testid={`lib-course-${course.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <button
          onClick={() => onNavigate(course)}
          className="flex-1 text-left min-w-0"
        >
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${st.bg} ${st.color} ${st.border}`}>
              {isCompleted ? 'Terminé' : st.label}
            </span>
            {course.language && (
              <span className="text-[10px] font-medium text-[#64748b] bg-white/5 border border-white/10 px-1.5 py-0.5 rounded-full uppercase">
                {course.language}
              </span>
            )}
          </div>
          <h3 className="text-white font-semibold text-sm leading-snug line-clamp-2 group-hover:text-[#20c997] transition-colors">
            {course.title}
          </h3>
          {course.description && (
            <p className="text-[#64748b] text-xs mt-1 line-clamp-2">{course.description}</p>
          )}
        </button>
        <CourseMenu course={course} onArchive={onArchive} onUnarchive={onUnarchive} onDelete={onDelete} />
      </div>

      {/* Footer info */}
      <div className="flex items-center justify-between pt-1 border-t border-[#2a2935]">
        <div className="flex items-center gap-3">
          {course.estimated_hours && (
            <span className="flex items-center gap-1 text-[10px] text-[#64748b]">
              <Clock size={10} /> {course.estimated_hours}h
            </span>
          )}
          {modules.length > 0 && (
            <span className="flex items-center gap-1 text-[10px] text-[#64748b]">
              <BookOpen size={10} /> {modules.length} modules
            </span>
          )}
        </div>
        {isCompleted && (
          <div className="flex items-center gap-1 text-[10px] text-[#20c997]">
            <CheckCircle size={10} /> Terminé
          </div>
        )}
        {course.targeted_referentials?.length > 0 && (
          <span className="text-[10px] text-[#a78bfa] truncate max-w-[120px]">
            {course.targeted_referentials[0]}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Main Library ──────────────────────────────────────────────────────────────
export default function Library() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSpace, spaces } = useSpace();

  const [courses, setCourses]           = useState([]);
  const [archivedCourses, setArchived]  = useState([]);
  const [allProgress, setProgress]      = useState({});
  const [loading, setLoading]           = useState(true);
  const [searchQ, setSearchQ]           = useState('');
  const [statusFilter, setStatusFilter] = useState('all');   // all | ongoing | done | archived | error | generating
  const [langFilter, setLangFilter]     = useState('');      // '' | 'fr' | 'en'
  const [sortBy, setSortBy]             = useState('recent'); // recent | title | hours
  const [showFilters, setShowFilters]   = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, aRes, pRes] = await Promise.all([
        axios.get(`${API}/courses`, { withCredentials: true }),
        axios.get(`${API}/courses?filter=archived`, { withCredentials: true }),
        axios.get(`${API}/courses/progress/all`, { withCredentials: true }),
      ]);
      setCourses(cRes.data || []);
      setArchived(aRes.data || []);
      setProgress(pRes.data || {});
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleArchive = async (courseId) => {
    try {
      await axios.put(`${API}/courses/${courseId}/archive`, {}, { withCredentials: true });
      toast.success('Cours archivé');
      loadData();
    } catch { toast.error('Erreur lors de l\'archivage'); }
  };

  const handleUnarchive = async (courseId) => {
    try {
      await axios.put(`${API}/courses/${courseId}/unarchive`, {}, { withCredentials: true });
      toast.success('Cours restauré');
      loadData();
    } catch { toast.error('Erreur de restauration'); }
  };

  const handleDelete = async (courseId, courseTitle) => {
    if (!window.confirm(`Supprimer définitivement "${courseTitle}" ?`)) return;
    try {
      await axios.delete(`${API}/courses/${courseId}`, { withCredentials: true });
      toast.success('Cours supprimé');
      loadData();
    } catch { toast.error('Erreur de suppression'); }
  };

  const handleNavigate = (course) => {
    if (course.archived_at) {
      toast.info('Ce cours est archivé. Restaurez-le depuis le menu ⋮ pour y accéder.');
      return;
    }
    if (course.status === 'generating') navigate(`/generate/${course.id}`);
    else if (course.status === 'ready') navigate(`/course/${course.id}`);
  };

  // All courses pool (active + archived based on filter)
  const pool = useMemo(() => {
    const activeCourses = activeSpace
      ? [...courses.filter(c => c.space_id === activeSpace.id), ...courses.filter(c => c.space_id !== activeSpace.id)]
      : courses;
    if (statusFilter === 'archived') return archivedCourses;
    return activeCourses;
  }, [courses, archivedCourses, activeSpace, statusFilter]);

  const filtered = useMemo(() => {
    let list = pool;

    // Status filter
    if (statusFilter === 'ongoing') list = list.filter(c => c.status === 'ready' && !allProgress[c.id]);
    else if (statusFilter === 'done') list = list.filter(c => c.status === 'ready' && allProgress[c.id] === true);
    else if (statusFilter === 'error') list = list.filter(c => c.status === 'error');
    else if (statusFilter === 'generating') list = list.filter(c => c.status === 'generating');
    // 'all' and 'archived' already handled above

    // Language filter
    if (langFilter) list = list.filter(c => c.language === langFilter);

    // Text search
    if (searchQ.trim().length > 0) {
      const q = searchQ.toLowerCase();
      list = list.filter(c =>
        c.title?.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q) ||
        c.topic?.toLowerCase().includes(q)
      );
    }

    // Sort
    if (sortBy === 'title') list = [...list].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    else if (sortBy === 'hours') list = [...list].sort((a, b) => (a.estimated_hours || 0) - (b.estimated_hours || 0));
    else list = [...list].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

    return list;
  }, [pool, statusFilter, langFilter, searchQ, sortBy, allProgress]);

  // Tab counts
  const allActive = activeSpace
    ? [...courses.filter(c => c.space_id === activeSpace.id), ...courses.filter(c => c.space_id !== activeSpace.id)]
    : courses;

  const counts = {
    all:        allActive.length,
    ongoing:    allActive.filter(c => c.status === 'ready' && !allProgress[c.id]).length,
    done:       allActive.filter(c => c.status === 'ready' && allProgress[c.id] === true).length,
    archived:   archivedCourses.length,
    error:      allActive.filter(c => c.status === 'error').length,
    generating: allActive.filter(c => c.status === 'generating').length,
  };

  const STATUS_TABS = [
    { id: 'all',        label: 'Tous' },
    { id: 'ongoing',    label: 'En cours' },
    { id: 'done',       label: 'Terminés' },
    { id: 'generating', label: 'En génération' },
    { id: 'archived',   label: 'Archivés' },
    { id: 'error',      label: 'Erreur' },
  ].filter(t => t.id === 'all' || counts[t.id] > 0);

  const activeFiltersCount = (langFilter ? 1 : 0) + (sortBy !== 'recent' ? 1 : 0);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 pt-6 pb-8 space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-black text-white">Bibliothèque</h1>
            <p className="text-[#64748b] text-sm mt-0.5">
              {loading ? '…' : `${counts.all + counts.archived} formation${counts.all + counts.archived !== 1 ? 's' : ''} au total`}
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="new-course-btn"
            className="flex items-center gap-2 bg-[#ff6b6b] hover:opacity-90 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-opacity"
          >
            <Plus size={15} /> Nouvelle
          </button>
        </div>

        {/* Search + Filters */}
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748b]" />
              <input
                type="text"
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder="Rechercher dans vos formations..."
                className="w-full bg-[#16161e] border border-[#2a2935] text-white placeholder-[#64748b] rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#20c997]/50 transition-colors"
                data-testid="library-search-input"
              />
              {searchQ && (
                <button onClick={() => setSearchQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-white">
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(v => !v)}
              data-testid="filters-toggle-btn"
              className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl border text-sm font-medium transition-all ${showFilters || activeFiltersCount > 0 ? 'bg-[#20c997]/10 border-[#20c997]/30 text-[#20c997]' : 'bg-[#16161e] border-[#2a2935] text-[#64748b] hover:text-white hover:border-[#3a3945]'}`}
            >
              <SlidersHorizontal size={15} />
              Filtres
              {activeFiltersCount > 0 && (
                <span className="w-4 h-4 bg-[#20c997] text-[#0f0e17] rounded-full text-[10px] font-bold flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            <button
              onClick={loadData}
              data-testid="refresh-btn"
              className="p-2.5 rounded-xl bg-[#16161e] border border-[#2a2935] text-[#64748b] hover:text-white hover:border-[#3a3945] transition-all"
            >
              <RefreshCw size={15} />
            </button>
          </div>

          {/* Advanced filters panel */}
          {showFilters && (
            <div className="bg-[#16161e] border border-[#2a2935] rounded-xl p-4 space-y-3" data-testid="filters-panel">
              <div className="flex flex-wrap gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-[#64748b] mb-1.5 font-bold">Langue</p>
                  <div className="flex gap-1.5">
                    {['', 'fr', 'en'].map(lang => (
                      <button
                        key={lang || 'all'}
                        onClick={() => setLangFilter(lang)}
                        data-testid={`lang-filter-${lang || 'all'}`}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${langFilter === lang ? 'bg-[#20c997] text-[#0f0e17]' : 'bg-[#0f0e17] border border-[#2a2935] text-[#64748b] hover:text-white'}`}
                      >
                        {lang === '' ? 'Toutes' : lang === 'fr' ? <><Globe size={10} className="inline mr-1" />FR</> : <><Globe size={10} className="inline mr-1" />EN</>}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-[#64748b] mb-1.5 font-bold">Tri</p>
                  <div className="flex gap-1.5">
                    {[{ id: 'recent', label: 'Récent' }, { id: 'title', label: 'A-Z' }, { id: 'hours', label: 'Durée' }].map(opt => (
                      <button
                        key={opt.id}
                        onClick={() => setSortBy(opt.id)}
                        data-testid={`sort-${opt.id}`}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${sortBy === opt.id ? 'bg-[#20c997] text-[#0f0e17]' : 'bg-[#0f0e17] border border-[#2a2935] text-[#64748b] hover:text-white'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {activeFiltersCount > 0 && (
                <button
                  onClick={() => { setLangFilter(''); setSortBy('recent'); }}
                  className="text-xs text-[#ff6b6b] hover:underline flex items-center gap-1"
                  data-testid="clear-filters-btn"
                >
                  <X size={11} /> Réinitialiser les filtres
                </button>
              )}
            </div>
          )}
        </div>

        {/* Status tabs */}
        <div className="flex gap-1 bg-[#16161e] border border-[#2a2935] rounded-xl p-1 overflow-x-auto no-scrollbar" data-testid="status-tabs">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                statusFilter === tab.id ? 'bg-[#ff6b6b] text-white' : 'text-[#64748b] hover:text-white'
              }`}
            >
              {tab.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full min-w-[18px] text-center ${statusFilter === tab.id ? 'bg-white/20' : 'bg-white/8'}`}>
                {counts[tab.id]}
              </span>
            </button>
          ))}
        </div>

        {/* Course grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-[#16161e] border border-[#2a2935] rounded-2xl h-36 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-[#16161e] border border-[#2a2935] rounded-2xl p-10 text-center" data-testid="library-empty">
            <BookOpen size={36} className="text-[#2a2935] mx-auto mb-3" />
            <p className="text-[#64748b] text-sm">
              {searchQ ? `Aucun résultat pour « ${searchQ} »` : 'Aucune formation dans cette catégorie.'}
            </p>
            {searchQ && (
              <button onClick={() => setSearchQ('')} className="mt-3 text-xs text-[#20c997] hover:underline">
                Effacer la recherche
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="library-grid">
            {filtered.map(course => (
              <CourseCard
                key={course.id}
                course={course}
                isCompleted={allProgress[course.id] === true}
                onNavigate={handleNavigate}
                onArchive={handleArchive}
                onUnarchive={handleUnarchive}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* Results count */}
        {!loading && filtered.length > 0 && (
          <p className="text-center text-xs text-[#64748b]" data-testid="results-count">
            {filtered.length} formation{filtered.length !== 1 ? 's' : ''} affichée{filtered.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </Layout>
  );
}
